import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { PerspectiveCamera, OrbitControls } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import * as THREE from 'three';
import { Vector3Tuple } from 'three';
import CanvasLights from '~/components/Canvas/CanvasLights';
import { AvatarInfo } from '~/types';
import { CanvasOptions } from '~/constants/Canvas';
import { useChatAvatarStore } from '~/store';
import Background from './Background';
import Character from './Character/Character';
import RobotCharacter from './Character/RobotCharacter';

interface ChatAvatarCanvasProps {
  avatars?: AvatarInfo[];
  background?: string;
}

export default function ChatAvatarCanvas({
  avatars = [],
  background = '/park.jpg',
}: ChatAvatarCanvasProps) {
  const { fov } = CanvasOptions;

  const cameraPosition: Vector3Tuple = useMemo(() => [0, 11, 18], []);
  const cameraTarget = useMemo(() => new THREE.Vector3(0, 11, 0), []);

  const resetAvatarState = useChatAvatarStore(
    (state) => state.resetAvatarState,
  );
  const cameraRef = useRef<THREE.PerspectiveCamera>(null);
  const controlsRef = useRef<React.ComponentRef<typeof OrbitControls> | null>(
    null,
  );

  useEffect(() => {
    return () => {
      resetAvatarState();
    };
  }, [resetAvatarState]);

  useEffect(() => {
    if (cameraRef.current && controlsRef.current) {
      cameraRef.current.position.set(...cameraPosition);

      controlsRef.current.target.copy(cameraTarget);
      controlsRef.current.update();
    }
  }, [cameraPosition, cameraTarget]);

  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.target.copy(cameraTarget);
      controlsRef.current.update();
    }
  }, [cameraTarget]);

  return (
    <Canvas
      css={
        !background &&
        css`
          background: #09090a;
        `
      }
    >
      <Suspense fallback={null}>
        <PerspectiveCamera
          ref={cameraRef}
          position={cameraPosition}
          fov={fov}
          makeDefault
        />

        <OrbitControls
          ref={controlsRef}
          camera={cameraRef.current || undefined}
          target={cameraTarget}
          maxPolarAngle={Math.PI / 1.8}
          minDistance={10} // Minimum zoom distance
          maxDistance={25}
          enablePan={false}
        />
        <CanvasLights />
        {background && <Background imageUrl={background} />}

        {avatars.map((avatar) => {
          const position = CanvasOptions.character_position as [
            number,
            number,
            number,
          ];

          if (avatar.id === 'robot') {
            return (
              <RobotCharacter
                key={avatar.id}
                id={avatar.id}
                visible={true}
                avatarUrl={avatar.url}
                position={position}
              />
            );
          } else {
            return (
              <Character
                key={avatar.id}
                avatar={avatar}
                visible={true}
                position={position}
              />
            );
          }
        })}
      </Suspense>
    </Canvas>
  );
}
